import { forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown, FileEntry, FileUpload, Form, Textbox, useForm } from "capitalrx-components-2.0/lib/forms";
import { Grid, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";

import { phoneNumberValidator } from "@/App/utils/validation/phone";

interface DirectMemberReimbursementCoordinationOfBenefitsFormProps {
  readonly smallFieldsColumns: number;
  readonly mediumFieldsColumns: number;
}

interface DMRCoordinationOfBenefitsForm {
  on_job_inquiry: "yes" | "no";
  other_insurance_plan: "yes" | "no";
  employer_insurance_name: string;
  id_number: string;
  phone_number: string;
  primary_carriers_explanation_of_benefits: FileEntry[];
}

export type DirectMemberReimbursementCoordinationOfBenefitsFormHandle = {
  submit: () => Promise<DMRCoordinationOfBenefitsForm>;
};

const TRANSLATION_PATH = "DIRECT_MEMBER_REIMBURSEMENT.directMemberReimbursementCoordinationOfBenefitsForm";

export const DirectMemberReimbursementCoordinationOfBenefitsForm = forwardRef<
  DirectMemberReimbursementCoordinationOfBenefitsFormHandle,
  DirectMemberReimbursementCoordinationOfBenefitsFormProps
>(({ smallFieldsColumns, mediumFieldsColumns }, ref) => {
  const { t: translate } = useTranslation();
  const form = useForm<DMRCoordinationOfBenefitsForm>({
    defaultValues: {},
  });

  useImperativeHandle(ref, () => ({
    submit: () =>
      new Promise<DMRCoordinationOfBenefitsForm>((resolve) => {
        form.handleSubmit(
          () => resolve(form.getValues()),
          () => resolve(null),
        )();
      }),
  }));

  return (
    <Form form={form}>
      <Stack spacing="medium">
        <Subheading>{translate(`${TRANSLATION_PATH}.coordinationOfBenefits`)}</Subheading>
        <Stack spacing="large">
          <Stack spacing="medium">
            <Body>{translate(`${TRANSLATION_PATH}.isJobRelated`)}</Body>
            <Grid>
              <Grid.Span columns={smallFieldsColumns}>
                <Dropdown.Field required name="on_job_inquiry" placeholder={translate(`${TRANSLATION_PATH}.select`)} hideLabel>
                  <Dropdown.Option value="yes">{translate(`${TRANSLATION_PATH}.isJobRelatedYes`)}</Dropdown.Option>
                  <Dropdown.Option value="no">{translate(`${TRANSLATION_PATH}.isJobRelatedNo`)}</Dropdown.Option>
                </Dropdown.Field>
              </Grid.Span>
            </Grid>
          </Stack>

          <Stack spacing="medium">
            <Body>{translate(`${TRANSLATION_PATH}.isCoveredByOtherGroupPlan`)}</Body>
            <Grid>
              <Grid.Span columns={smallFieldsColumns}>
                <Dropdown.Field required name="other_insurance_plan" placeholder={translate(`${TRANSLATION_PATH}.select`)} hideLabel>
                  <Dropdown.Option value="yes">{translate(`${TRANSLATION_PATH}.isCoveredByOtherGroupPlanYes`)}</Dropdown.Option>
                  <Dropdown.Option value="no">{translate(`${TRANSLATION_PATH}.isCoveredByOtherGroupPlanNo`)}</Dropdown.Option>
                </Dropdown.Field>
              </Grid.Span>
            </Grid>
          </Stack>

          <Stack spacing="medium">
            <Semibold>{translate(`${TRANSLATION_PATH}.groupInsurancePlanInformation`)}</Semibold>
            <Grid>
              <Grid.Span columns={mediumFieldsColumns}>
                <Textbox.Field label={translate(`${TRANSLATION_PATH}.companyName`)} name="employer_insurance_name" required />
              </Grid.Span>
              <Grid.Break />
              <Grid.Span columns={smallFieldsColumns}>
                <Textbox.Field label={translate(`${TRANSLATION_PATH}.idNumber`)} name="id_number" required />
              </Grid.Span>
              <Grid.Break />
              <Grid.Span columns={smallFieldsColumns}>
                <Textbox.Field
                  label={translate(`${TRANSLATION_PATH}.phoneNumber`)}
                  name="phone_number"
                  required
                  instructions="123-456-7890"
                  validator={(value) => phoneNumberValidator(value, translate)}
                />
              </Grid.Span>
            </Grid>
          </Stack>

          <Stack spacing={"medium"}>
            <Semibold>{translate(`${TRANSLATION_PATH}.primaryCarrierEOB`)}</Semibold>
            <Body>{translate(`${TRANSLATION_PATH}.eobUploadInstructions`)}</Body>
            <FileUpload.Field
              label={translate(`${TRANSLATION_PATH}.eobSelectUpload`)}
              name="primary_carriers_explanation_of_benefits"
              allowedExtensions={[".pdf", ".png", ".jpg", ".heic"]}
              maxSize={4500000}
              required
              allowMultiple
            />
          </Stack>
        </Stack>
      </Stack>
    </Form>
  );
});
